import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {of, Subject} from "rxjs";
import {ModalV2Component} from "../modal-v2/modal-v2.component";
import {LessonProgressService} from "../../service/lesson-progress.service";
import {filter, switchMap, tap} from "rxjs/operators";
import {RoomProgressReport} from "../../model/server";
import {BooksRestService} from "../../service/books-service";
import {
  BookPageSelection,
  BookRendererControllerService,
  BookSelection
} from "../../service/book-renderer-controller.service";
import {Course} from "../../model/courses";
import {ContentItemType, Course as BookCourse} from "../../model/books";

@Component({
  selector: 'app-open-planned-book',
  templateUrl: './open-planned-book.component.html',
  styleUrls: ['./open-planned-book.component.css']
})
export class OpenPlannedBookComponent implements OnInit {
  private static readonly ALWAYS_OCC_VAR_NAME = "alwaysOpenOccEbooks";
  private static readonly EN3_COURSE_CODE = "en3";
  private static readonly EN3_ONLINE_COURSE_CODE = "en3-online";
  private static readonly ONLINE_SUFFIX = "_online";

  @ViewChild("occModal", {static: true})
  occModal: ModalV2Component;

  alwaysOcc = false;
  private pendingSelection: BookPageSelection;
  private pendingOccSelection: BookPageSelection;

  private partiSubject = new Subject<string>();
  private progress: RoomProgressReport;
  private books: BookCourse[];
  @Input()
  set participantUuid(value: string) {
    this.partiSubject.next(value);
  }
  constructor(
    private progressService: LessonProgressService,
    private rest: BooksRestService,
    private bookRendererCtrl: BookRendererControllerService
  ) {
    this.partiSubject.pipe(
      filter(uuid => !!uuid),
      switchMap(uuid => this.loadBooks(uuid)),
      switchMap(uuid => progressService.readProgress(uuid)),
      tap<RoomProgressReport>(progress => this.progress = progress)
    ).subscribe();
  }

  ngOnInit() {
  }

  getBookPageSelection() {
    if (
      !this.progress
      || !this.books
      || !this.progress.details.course
      || !this.progress.details.startPage
    ) return null;
    const pageNumber = Number.parseInt(this.progress.details.startPage);
    if (!pageNumber) return null;
    const lessonCourse = Course.byName(this.progress.details.course);
    if (!lessonCourse) return null;
    const bookCourse = this.books.find(bookCourse => bookCourse.code === lessonCourse.code);
    if (!bookCourse) return null;
    const stageFound = bookCourse.stages.find( stage => stage["content-items"].find(item =>
      item["page-from"] <= pageNumber
      && pageNumber <= item["page-to"]
      && (!item.type || item.type === ContentItemType.TEXT)
      )
    );
    if (!stageFound) return null;
    return new BookPageSelection(
      new BookSelection(bookCourse, stageFound), pageNumber
    );
  }

  hasPlannedJump() {
    return !!this.getBookPageSelection();
  }

  doJump(){
    const selection = this.getBookPageSelection();
    const occSelection = this.getOccBookPageSelection(selection);
    if (!occSelection) {
      this.bookRendererCtrl.openBookPage(selection);
      return;
    }
    if (this.isAlwaysOccEnabled()) {
      this.bookRendererCtrl.openBookPage(occSelection);
      return;
    }
    this.pendingSelection = selection;
    this.pendingOccSelection = occSelection;
    this.alwaysOcc = false;
    this.occModal.show();
  }

  private getOccBookPageSelection(selection: BookPageSelection): BookPageSelection {
    if (!selection) return null;
    if (selection.book.course.code !== OpenPlannedBookComponent.EN3_COURSE_CODE) return null;
    const occCourse = this.books.find(book => book.code === OpenPlannedBookComponent.EN3_ONLINE_COURSE_CODE);
    if (!occCourse) return null;
    const occStageCode = selection.book.stage.code + OpenPlannedBookComponent.ONLINE_SUFFIX;
    const occStage = occCourse.stages.find(stage => stage.code === occStageCode);
    if (!occStage) return null;
    return new BookPageSelection(new BookSelection(occCourse, occStage), selection.page);
  }

  private isAlwaysOccEnabled() {
    return localStorage.getItem(OpenPlannedBookComponent.ALWAYS_OCC_VAR_NAME) === "true";
  }

  getPlannedStageNumber() {
    if (!this.pendingSelection) return "";
    const match = this.pendingSelection.book.stage.code.match(/_(\d+)$/);
    return match ? match[1] : "";
  }

  openStandard() {
    this.occModal.hide();
    this.bookRendererCtrl.openBookPage(this.pendingSelection);
  }

  continueWithOcc() {
    if (this.alwaysOcc) {
      localStorage.setItem(OpenPlannedBookComponent.ALWAYS_OCC_VAR_NAME, "true");
    }
    this.occModal.hide();
    this.bookRendererCtrl.openBookPage(this.pendingOccSelection);
  }

  private loadBooks(uuid: string) {
    if (this.books) return of(uuid);
    return this.rest.listProducts(uuid).pipe(
      tap( books => this.books = books),
      switchMap( _ => of(uuid))
    );
  }

  getSelectionDescription() {
    const bookPageSelection = this.getBookPageSelection();
    return `${bookPageSelection.book.course.courseName}, p. ${bookPageSelection.page}`;
  }
}
